import { useMutation, useQuery } from '@tanstack/react-query'
import { FiAlertTriangle, FiArrowRight, FiArrowUp, FiCheck, FiEdit, FiExternalLink, FiLogOut, FiPlus, FiTrash } from 'react-icons/fi'
import { Formik, Form, Field } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import {
	Tab, Tabs, TabList, TabPanel
} from 'react-tabs'
import Card from '../../../components/Card'
import { api, authHeaders } from '../../../services/api'
import Spinner from '../../../components/Spinner'
import { Button, Select, Textbox } from '../../../components/Form'
import Modal from '../../../components/Modal'
import Table from '../../../components/Table'
import { pluralize } from '../../../util/string'
import maskUtils from '../../../util/masks'
import formUtils from '../../../util/form'
import { getAge } from '../../../util/date'
import Yup from '../../../services/yup'
import useQueryParams from '../../../hooks/useQuery'
import PasswordTextbox from '../../../components/Form/PasswordTextbox'
import { GlobalContext } from '../../../contexts/GlobalContext'
import yup from '../../../services/yup'

const baseValidation = Yup.object({
	nome: Yup.string(),
	data_nascimento: Yup.string().date().required('Especifique a data de nascimento.'),
	whatsapp: Yup.string().mobile(),
	gestao_independente: Yup.object({ label: Yup.string(), value: Yup.number() })
})

const baseInitialValues = {
	id: null,
	'membro-familia': '',
	nome: '',
	data_nascimento: '',
	whatsapp: '',
	email: '',
	relacao: '',
	sexo: '',
	gestao_independente: ''
}

const opcoesGestaoProntuario = [
	{ value: 1, label: 'Esta pessoa irá gerenciar seu próprio prontuário.' },
	{ value: 2, label: 'Eu irei gerenciar o prontuário desta pessoa.' }
]

export default function () {
	const history = useHistory()

	const { user } = useContext(GlobalContext)

	const { membro } = useQueryParams()

	const [grupoMembros, setGrupoMembros] = useState(null)
	const [grupoMembrosRemovidos, setGrupoMembrosRemovidos] = useState(null)
	const [isLoadingMembros, setIsLoadingMembros] = useState(false)
	const [showModalCadastro, setShowModalCadastro] = useState(false)
	const [grupoSaidaConfirmacao, setGrupoSaidaConfirmacao] = useState(null)
	const [planoSelecionado, setPlanoSelecionado] = useState(null)
	const [moduloSelecionado, setModuloSelecionado] = useState(null)
	const [assinaturaSelecionadaId, setAssinaturaSelecionadaId] = useState(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [validation, setValidation] = useState(baseValidation)
	const [initialValues, setInitialValues] = useState(baseInitialValues)
	const [proprietarioDoEmail, setProprietarioDoEmail] = useState(null)
	const [pessoaExistenteConfirmada, setPessoaExistenteConfirmada] = useState(false)
	const [indiceTabAtiva, setIndiceTabAtiva] = useState(Number(membro) || 0)

	const { data: pessoaModulos, refetch: refetchPessoaModulos } = useQuery({
		queryKey: ['get-modulos-contratados-ativos'],
		queryFn: async () => {
			let response = await api.get('pessoa', {
				...authHeaders(),
				params: {
					modulos_contratados: 1
				}
			})

			response = response.filter(mod => mod.status === 'authorized')

			const modulosTitular = response.filter(mod => mod.e_titular)

			if (modulosTitular.length === 1) {
				const modulo = modulosTitular[0]

				setModuloSelecionado({
					label: modulo.descricao,
					value: modulo.modulo_id
				})
			}

			return response
		}
	})

	const { data: familia } = useQuery({
		queryKey: ['obter-familiares'],
		queryFn: async () => {
			const response = await api.get('familia', authHeaders())

			return response
		},
		initialData: []
	})

	const { data: relacoes } = useQuery({
		queryKey: ['obter-relacoes-pessoa'],
		queryFn: async () => {
			const response = await api.get('tipo_relacao', {
				params: {
					tipo_plano: 'familia'
				}
			})

			return response
		}
	})

	const { mutate: addMembroGrupo } = useMutation({
		mutationFn: async data => {
			setIsSubmitting(true)

			data = formUtils.extractFormValues(data)

			if (data['membro-familia']) {
				data = {
					membro_familia: data['membro-familia']
				}
			}

			data.plano_id = planoSelecionado.id
			data.gestao_independente = data.gestao_independente === 2 ? 0 : 1
			data.pessoa_existente_id = proprietarioDoEmail?.pessoa.id

			await api.post('assinatura/grupo', data, authHeaders())

			obterGrupoPorAssinatura()

			setShowModalCadastro(false)

			toast.success('Membro do grupo cadastro com sucesso.')

			setIsSubmitting(false)
		},
		onError: e => {
			toast.error(e.msg)

			setIsSubmitting(false)
		}
	})

	const { mutate: updateMembroGrupo } = useMutation({
		mutationFn: async data => {
			setIsSubmitting(true)

			data = formUtils.extractFormValues(data)

			data.gestao_independente = data.gestao_independente === 2 ? 0 : 1
			data.pessoa_existente_id = proprietarioDoEmail?.pessoa.id

			await api.put(`assinatura/grupo/${data.id}/${user.pessoa.id}`, data, authHeaders())

			obterGrupoPorAssinatura()

			setShowModalCadastro(false)
			setInitialValues(baseInitialValues)

			toast.success('Membro do grupo atualizado com sucesso.')

			setIsSubmitting(false)
		},
		onError: e => {
			toast.error(e.msg)

			setIsSubmitting(false)
		}
	})

	const { mutate: removeMembroGrupo } = useMutation({
		mutationFn: async membro => {
			await api.delete(`assinatura/grupo/${assinaturaSelecionadaId}/${membro.id}`, authHeaders())

			obterGrupoPorAssinatura()

			toast.success('O membro foi removido do grupo.')
		},
		onError: e => {
			toast.error(e.msg)
		}
	})

	const { mutate: handleRestaurarMembro, isPending: isRestaurandoMembro } = useMutation({
		mutationFn: async membro => {
			await api.patch(`assinatura/grupo/${assinaturaSelecionadaId}/${membro.id}`, {}, authHeaders())

			obterGrupoPorAssinatura()

			toast.success('O membro foi restaurado no grupo.')
		}
	})

	const { mutate: handleExecutarSaidaDoGrupo, isPending: isRemovendoDoGrupo } = useMutation({
		mutationFn: async values => {
			try {
				await api.put('grupo/saida', {
					password: values.senha,
					assinatura_id: values.assinatura_id,
					assinatura_membro_familia_id: values.assinatura_membro_familia_id
				}, authHeaders())

				toast.success('Você não faz mais parte do grupo selecionado.')

				setGrupoSaidaConfirmacao(null)

				refetchPessoaModulos()
			} catch (e) {
				toast.error(e.msg)
			}
		}
	})

	async function verificarEmailMembro(email) {
		try {
			const response = await api.get(`pessoa/email/${email}`, authHeaders())

			if (response) {
				setProprietarioDoEmail(response)
			}
		} catch (e) {
			toast.error(e.msg)
		}
	}

	async function obterGrupoPorAssinatura() {
		try {
			setIsLoadingMembros(true)

			const { assinatura_id } = pessoaModulos.find(item => item.modulo_id === moduloSelecionado.value)

			setAssinaturaSelecionadaId(assinatura_id)

			// Obtendo o plano da assinatura selecionado

			const plano = await api.get(`plano/por_assinatura/${assinatura_id}`, authHeaders())

			setPlanoSelecionado(plano)

			// Obtendo membros da assinatura selecionada

			const grupo = await api.get(`grupo/por_assinatura/${assinatura_id}`, authHeaders())
			const grupoRemovido = await api.get(`grupo/por_assinatura/${assinatura_id}/removidas`, authHeaders())

			setGrupoMembros(grupo.map(item => ({
				id: item.pessoa.id,
				nome: item.pessoa.nome,
				sexo: item.pessoa.sexo,
				email: item.pessoa.contato?.find(c => c.tipo_contato_id === 6)?.contato || '',
				whatsapp: item.pessoa.contato?.find(c => c.tipo_contato_id === 4)?.contato || '',
				data_nascimento: item.pessoa.data_nascimento ? `${format(new Date(item.pessoa.data_nascimento), 'dd/MM/yyyy')}` : 'não informado',
				data_nascimento_e_idade: item.pessoa.data_nascimento ? `${format(new Date(item.pessoa.data_nascimento), 'dd/MM/yyyy')} (${getAge(item.pessoa.data_nascimento)} anos)` : '-',
				relacao: item.pessoa.familia.relacao.descricao,
				status_aceitacao: item.token_consentimento ? 'pendente' : 'aceito',
				gestao_independente: item.gestao_independente,
				compartilhado_com_titular: item.compartilhado_com_titular
			})))

			setGrupoMembrosRemovidos(grupoRemovido.map(item => ({
				id: item.pessoa.id,
				nome: item.pessoa.nome,
				data_nascimento: item.pessoa.data_nascimento ? `${format(new Date(item.pessoa.data_nascimento), 'dd/MM/yyyy')} (${getAge(item.pessoa.data_nascimento)} anos)` : '-',
				relacao: item.pessoa.familia.relacao.descricao,
			})))
		} catch (e) {
			toast.error(e.msg)
		} finally {
			setIsLoadingMembros(false)
		}
	}

	function handleAcessarProntuario(item) {
		history.push({
			pathname: '/prontuario/membro',
			state: {
				membro_id: item.id
			}
		})
	}

	function handlePrepareEdit(data) {
		const opcaoGerenciamentoIndependente = opcoesGestaoProntuario.find(o => (data.gestao_independente && o.value === 1) || (!data.gestao_independente && o.value === 2))

		const relacao = relacoes.find(r => r.descricao === data.relacao)

		setInitialValues({
			id: data.id,
			gestao_independente: opcaoGerenciamentoIndependente,
			'membro-familia': '',
			nome: data.nome,
			data_nascimento: data.data_nascimento,
			whatsapp: data.whatsapp,
			email: data.email,
			relacao: { label: relacao.descricao, value: relacao.id },
			sexo: data.sexo ? { label: data.sexo === 'M' ? 'Masculino' : 'Feminino', value: data.sexo } : ''
		})
	}

	function handleSubmitMembroGrupo(data) {
		if (showModalCadastro) {
			addMembroGrupo(data)
		} else {
			updateMembroGrupo(data)
		}
	}

	function handleSelecionarUsuarioExistente() {
		setPessoaExistenteConfirmada(true)

		setValidation(Yup.object({
			relacao: Yup.object({
				label: Yup.string(),
				value: Yup.number()
			})
		}))
	}

	useEffect(() => {
		if (moduloSelecionado) {
			obterGrupoPorAssinatura()
		}
	}, [moduloSelecionado])

	const vagasOfertadas = planoSelecionado?.recursos.find(item => item.identificador === 'pessoas')?.quantidade - 1
	const vagasConsumidas = grupoMembros?.length
	const vagasRestantes = vagasOfertadas && grupoMembros ? vagasOfertadas - vagasConsumidas : null
	const gruposTitular = pessoaModulos?.filter(mod => mod.e_titular)

	const gruposMembro = pessoaModulos?.filter(mod => !mod.e_titular).map(item => ({
		...item,
		url_consentimento: item.token_consentimento ? `/auth/membro_familia/consentimento/${item.token_consentimento}` : null
	})) || []

	const pessoasDisponiveisParaInclusao = grupoMembros ? familia.filter(item => !grupoMembros.some(i => i.id === item.pessoa.id)) : []

	const emEdicao = !!initialValues?.nome

	return (
		<>
			<div className="flex flex-col text-slate-800 sm:mx-4 sm:my-6 md:my-12 min-h-[46vh] leading-loose">
				<Card className="flex flex-col">
					<h1 className="text-slate-800 mb-4">Seus grupos</h1>

					{gruposMembro.length > 0 && (
						<p
							className="mb-4"
							dangerouslySetInnerHTML={{
								__html: `Você é membro do grupo de <span class="font-bold">${gruposMembro?.map(item => item.titular.nome).join(', ')}.</span>`
							}}
						/>
					)}

					<Tabs
						selectedIndex={indiceTabAtiva}
						onSelect={indiceTab => setIndiceTabAtiva(indiceTab)}
					>
						<TabList>
							<Tab>Titular</Tab>
							<Tab>Membro</Tab>
						</TabList>

						<TabPanel>
							{!!gruposTitular?.length ? (
								<>
									<p className="mb-4" dangerouslySetInnerHTML={{
										__html: 'Gerencie aqui seus grupos de usuários para que eles também tenham acesso às nossas soluções.'
									}} />

									<Select
										withoutForm
										name="modulo"
										label="Selecione o módulo"
										options={gruposTitular.map(item => ({
											label: item.descricao,
											value: item.modulo_id
										}))}
										onChange={setModuloSelecionado}
										value={moduloSelecionado}
									/>
								</>
							) : gruposTitular === undefined ? (
								<Spinner />
							) : (
								<div className="text-slate-700 bg-yellow-200 py-2 px-3 rounded-md flex flex-col lg:flex-row justify-between" style={{ fontSize: 13 }}>
									<div className="italic flex items-center gap-1 grid grid-cols-[24px_auto]">
										<FiAlertTriangle size={18} />
										Você não é assinante de nenhum plano atualmente.
									</div>

									<Link to="/dashboard?planos_disponiveis=1#modulos-disponiveis-title" className="flex gap-1 items-center self-end group">
										Conheça nossas soluções
										<FiArrowRight size={16} className="group-hover:translate-x-1 transition-all" />
									</Link>
								</div>
							)}

							{!!moduloSelecionado && (
								<div className="mt-4 flex flex-col">
									{vagasRestantes > 0 && (
										<Button onClick={() => { setShowModalCadastro(true) }} className="w-fit" disabled={vagasRestantes === 0}>
											Incluir membro no grupo
											<FiPlus size={16} />
										</Button>
									)}

									<p className="font-medium text-lg mt-2">Membros do seu grupo</p>

									{vagasRestantes > 0 ? (
										<p
											className="text-sm text-right mt-3"
											dangerouslySetInnerHTML={{
												__html: `Você ainda pode incluir <span class="font-bold">${pluralize(vagasRestantes, 'pessoa', 'pessoas')}</span> em seu grupo.`
											}}
										/>
									) : !!planoSelecionado ? (
										<div
											className="text-sm mt-3 bg-yellow-200 rounded-md p-2 w-fit text-center"
										>
											Você atingiu o limite de membros em seu grupo.
											{/* Ao incluir novos membros, será acrestando em sua fatura o valor de {maskUtils.maskApply.currency(planoSelecionado.valor_membro_extra / 100)} por membro adicionado. */}
										</div>
									) : null}
								</div>
							)}

							{isLoadingMembros || isRestaurandoMembro || isRemovendoDoGrupo ? (
								<Spinner />
							) : !!grupoMembros ? (
								<>
									<Table
										headers={[
											{ name: 'nome', value: 'Nome' },
											{ name: 'data_nascimento_e_idade', value: 'Data de nascimento', centered: true },
											{ name: 'relacao', value: 'Parentesco', centered: true },
											{ name: 'status_aceitacao', value: 'Convite', centered: true }
										]}
										data={grupoMembros}
										actions={[
											{
												action: removeMembroGrupo,
												name: 'Excluir membro',
												icon: FiTrash,
												iconSize: 16,
												confirmation: 'Deseja realmente excluir este membro?'
											},
											{
												action: handleAcessarProntuario,
												name: 'Acessar prontuário',
												icon: FiExternalLink,
												iconSize: 16,
												checkDisabled: item => !item.compartilhado_com_titular,
												disabledTooltip: item => `<h1 class="font-bold" style="font-size: 17px">Sem acesso</h1><p>Por questões de privacidade, <span class="font-bold">${item.nome}</span> não compartilhou o prontuário com você.</p>`
											},
											{
												action: handlePrepareEdit,
												name: 'Editar membro',
												icon: FiEdit,
												iconSize: 16
											}
										]}
										emptyLabel="Nenhum membro foi incluído."
									/>

									{!!grupoMembrosRemovidos?.length && (
										<details className="mt-6">
											<summary className="list-none appearance-none cursor-pointer border border-slate-400 px-4 py-2 rounded-md">
												<span className="font-medium text-slate-800" style={{ fontSize: 14 }}>Restaurar membros</span>
											</summary>

											<Table
												headers={[
													{ name: 'nome', value: 'Nome' },
													{ name: 'data_nascimento', value: 'Data de nascimento', centered: true },
													{ name: 'relacao', value: 'Parentesco', centered: true }
												]}
												data={grupoMembrosRemovidos}
												actions={[
													{
														action: handleRestaurarMembro,
														name: 'Restaurar membro',
														icon: FiArrowUp,
														iconSize: 16,
														confirmation: 'Deseja realmente restaurar este membro?'
													}
												]}
												emptyLabel="Nenhum membro da família foi removido deste plano."
											/>
										</details>
									)}
								</>
							) : null}
						</TabPanel>

						<TabPanel>
							<div className="flex flex-col">
								<div className="flex gap-1 items-center">
									Aqui você pode visualizar os grupos dos quais faz parte e optar por sair, clicando no ícone à direita da linha correspondente. Também é possível ver os convites com aceitação pendente.
								</div>

								<Table
									headers={[
										{ name: 'titular', value: 'Titular', centered: true },
										{ name: 'modulo', value: 'Módulo', centered: true }
									]}
									keyProp="assinatura_id"
									data={gruposMembro.map(grupo => ({
										...grupo,
										titular: grupo.titular.nome,
										modulo: grupo.descricao
									}))}
									actions={item => !item.url_consentimento ? [
										{
											action: setGrupoSaidaConfirmacao,
											name: 'Sair deste grupo',
											icon: FiLogOut,
											iconSize: 16
										}
									] : [
										{
											action: () => {
												history.push(item.url_consentimento)
											},
											name: 'Aceitar o convite',
											icon: FiCheck,
											iconSize: 16
										}
									]}
								/>
							</div>
						</TabPanel>
					</Tabs>
				</Card>
			</div>

			<Modal
				isOpen={showModalCadastro || emEdicao}
				handleClose={() => {
					setShowModalCadastro(false)
					setInitialValues(baseInitialValues)
				}}
				title={emEdicao ? 'Edição de membro' : 'Novo membro de grupo'}
			>
				<div className="flex flex-col gap-2">
					<Formik
						onSubmit={handleSubmitMembroGrupo}
						initialValues={initialValues}
						validationSchema={validation}
						enableReinitialize
					>
						{({ setFieldValue, values }) => {
							const temPessoaSelecionada = !!values['membro-familia']
							const valorAcrescimo = planoSelecionado.periodo === 'mensal' ?
								planoSelecionado.valor_membro_extra :
								planoSelecionado.valor_membro_extra * 12

							return (
								<Form className="flex flex-col">
									{vagasRestantes === 0 && (
										<div className="bg-yellow-300 p-3 rounded-md mx-2 mb-6">
											Você já atingiu o limite de usuários permitidos em seu plano atual. A partir de agora, cada pessoa incluída em seu grupo terá um custo adicional de <span className="font-bold">{maskUtils.maskApply.currency(valorAcrescimo / 100)}</span> em sua fatura {planoSelecionado.periodo}.
										</div>
									)}

									<span className="font-medium mx-3" style={{ fontSize: 14 }}>
										Quem irá gerenciar o prontuário desta pessoa?
									</span>

									<Select
										name="gestao_independente"
										label=""
										containerStyle={{ marginBottom: 30, maxWidth: 380 }}
										options={opcoesGestaoProntuario}
										onChange={(selected, meta) => {
											if (selected === undefined) {
												setFieldValue(meta.name, '')
												setValidation(baseValidation)
												return
											}

											setFieldValue(meta.name, selected)

											const gestaoIndependente = selected.value === 1

											setValidation(baseValidation.concat(
												Yup.object({
													email: gestaoIndependente ?
														Yup.string().email('E-mail inválido.').required('Informe o e-mail desta pessoa.') :
														Yup.string().email('E-mail inválido.')
												})
											))
										}}
									/>

									{!!pessoasDisponiveisParaInclusao?.length && values.gestao_independente !== '' && !emEdicao && (
										<>
											<Select
												name="membro-familia"
												label="Selecione uma pessoa da lista"
												onChange={(selected, meta) => {
													setValidation(Yup.object({
														gestao_independente: Yup.object({ label: Yup.string(), value: Yup.number() })
													}))

													setFieldValue(meta.name, selected)
												}}
												options={pessoasDisponiveisParaInclusao.map(item => ({
													label: `${item.pessoa.nome} (${item.relacao.descricao})`,
													value: item.pessoa.id
												}))}
											/>

											{!temPessoaSelecionada && (
												<div className="grid grid-cols-[auto_12rem_auto] items-center gap-4 mx-2 mt-8 mb-6">
													<div className="border-t-2 w-full border-slate-300"></div>
													<div className="text-center font-medium">ou convide um novo usuário</div>
													<div className="border-t-2 w-full border-slate-300"></div>
												</div>
											)}
										</>
									)}

									{!temPessoaSelecionada && values.gestao_independente !== '' && (
										<>
											{proprietarioDoEmail ? (
												<div className="flex flex-col gap-2 mx-2">
													<p>Esta é a pessoa que você está buscando?</p>

													<div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end">
														<div
															className="flex flex-col lg:flex-row gap-3 items-center border border-slate-300 py-2 px-3 rounded-md w-fit hover:bg-emerald-100 transition-all cursor-pointer"
															onClick={() => { handleSelecionarUsuarioExistente(proprietarioDoEmail) }}
														>
															{proprietarioDoEmail.avatar?.link && <img src={proprietarioDoEmail.avatar?.link} alt="" className="w-16 h-16 object-cover rounded-full" />}

															<div className="flex flex-col gap-1">
																<span className="font-semibold">{proprietarioDoEmail.pessoa.nome}</span>
																<span className="text-slate-400" style={{ fontSize: 12 }}>{proprietarioDoEmail.crm}</span>
															</div>

															<div className="text-emerald-500 flex gap-1 items-center">
																<FiCheck size={26} className="lg:ml-3 hidden lg:block" />
																<FiCheck size={21} className="lg:ml-3 lg:hidden" />
																<span className="lg:hidden" style={{ fontSize: 12 }}>Selecionar</span>
															</div>
														</div>

														{pessoaExistenteConfirmada ? (
															<Select
																name="relacao"
																label="Selecione o parentesco"
																onChange={(selected, meta) => {
																	setFieldValue(meta.name, selected)
																}}
																options={relacoes.map(item => ({
																	label: item.descricao,
																	value: item.id
																}))}
																containerClass="lg:max-w-56"
															/>
														) : (
															<Button className="white" onClick={() => { setProprietarioDoEmail(null) }}>
																Não é esta pessoa.
															</Button>
														)}
													</div>
												</div>
											) : (
												<>
													<div className="flex flex-col md:flex-row">
														<Textbox
															name="nome"
															label="Nome completo"
															disabled={values['membro-familia']}
														/>

														<Select
															name="relacao"
															label="Selecione o parentesco"
															onChange={(selected, meta) => {
																setFieldValue(meta.name, selected)
															}}
															options={relacoes.map(item => ({
																label: item.descricao,
																value: item.id
															}))}
															containerClass="lg:max-w-56"
														/>

														<Select
															name="sexo"
															label="Selecione o sexo"
															onChange={(selected, meta) => {
																setFieldValue(meta.name, selected)
															}}
															options={[
																{ label: 'Masculino', value: 'M' },
																{ label: 'Feminino', value: 'F' }
															]}
															containerClass="lg:max-w-56"
														/>

														<Textbox
															name="data_nascimento"
															label="Data de nascimento"
															mask={maskUtils.date}
															containerClass="lg:max-w-56"
															disabled={values['membro-familia']}
														/>
													</div>

													<div className="flex flex-col md:flex-row">
														<Textbox
															name="email"
															label="E-mail"
															className="lg:col-start-1"
															containerClass="lg:max-w-80"
															onBlur={e => {
																if (e.target.value) {
																	verificarEmailMembro(e.target.value)
																}
															}}
															disabled={values['membro-familia']}
															hint={values.gestao_independente && values.gestao_independente?.value === 1 ? 'Você deve informar o e-mail desta pessoa para que possamos enviar os dados de acesso.' : 'Este e-mail é opcional.'}
														/>

														<Textbox
															name="whatsapp"
															label="WhatsApp"
															mask={maskUtils.mobile}
															containerClass="lg:max-w-56"
															disabled={values['membro-familia']}
														/>
													</div>
												</>
											)}
										</>
									)}

									{values.nome && (
										<>
											{values.gestao_independente.value === 2 ? (
												<div
													className="mx-2 mt-3 text-slate-800"
													dangerouslySetInnerHTML={{
														__html: `Você poderá gerenciar o prontuário desta pessoa. Caso queira que ela também possa administrar o prontuário, informe o e-mail de <span class="font-semibold">${values.nome}</span> e nós enviaremos os dados de acesso ao sistema.`
													}}
												/>
											) : values.gestao_independente.value === 1 ? (
												<div
													className="mx-2 mt-3 text-slate-800"
													dangerouslySetInnerHTML={{
														__html: `Apenas esta pessoa poderá gerenciar seu próprio prontuário. Informe o e-mail de <span class="font-semibold">${values.nome}</span> e nós enviaremos os dados de acesso ao sistema.`
													}}
												/>
											) : null}
										</>
									)}

									{values.gestao_independente !== '' && (
										<div className="flex justify-end mt-4">
											<Button type="submit" loading={isSubmitting}>
												{emEdicao ? 'Atualizar membro do grupo' : 'Cadastrar membro no grupo'}
											</Button>
										</div>
									)}

									<Field type="hidden" name="id" />
								</Form>
							)
						}}
					</Formik>
				</div>
			</Modal>

			<Modal
				isOpen={!!grupoSaidaConfirmacao}
				title="Confirmação de exclusão"
				handleClose={() => {
					setGrupoSaidaConfirmacao(null)
				}}
			>
				<div className="mx-2 flex flex-col gap-2">
					<p className="mx-2">Ao sair deste grupo, você perderá acesso ao módulo <span className="font-semibold">{grupoSaidaConfirmacao?.modulo}</span>.</p>
					<p className="mx-2 mt-4">Informe sua senha para confirmar sua saída do grupo.</p>

					{grupoSaidaConfirmacao && (
						<Formik
							onSubmit={handleExecutarSaidaDoGrupo}
							initialValues={{
								senha: '',
								assinatura_id: grupoSaidaConfirmacao.assinatura_id,
								assinatura_membro_familia_id: grupoSaidaConfirmacao.assinatura_membro_familia_id
							}}
							validationSchema={yup.object({ senha: Yup.string().required('Informe sua senha.') })}
						>
							{({ isSubmitting }) => (
								<Form className="flex flex-col gap-4">
									<PasswordTextbox name="senha" label="Senha" containerClass="max-w-80" />

									<Field type="hidden" name="assinatura_id" />
									<Field type="hidden" name="assinatura_membro_familia_id" />

									<Button type="submit" className="md:self-end md:w-fit" loading={isSubmitting}>
										Confirmar saída
										<FiLogOut size={16} />
									</Button>
								</Form>
							)}
						</Formik>
					)}
				</div>
			</Modal>
		</>
	)
}
